/**
 * Analytics hooks — GDD §15, docs/05 KPI table.
 *
 * Defined as engine events now so the KPI definitions cannot drift from what the
 * game actually measures. The transport adapter lands in GOD-3; until then the
 * sink is a no-op collector that tests can assert against.
 */

import type { EventLog } from './events.js';
import type { GameState } from './state.js';

export type KpiName =
  | 'tutorialCompletion'
  | 'firstAdventureCompletion'
  | 'secondAdventureStart'
  | 'sessionLength'
  | 'npcRevisit'
  | 'choiceReplay'
  | 'discoveryShare';

export interface AnalyticsRecord {
  readonly kpi: KpiName;
  readonly value: number;
  readonly tick: number;
  readonly meta?: Readonly<Record<string, string | number | boolean>>;
}

export interface AnalyticsSink {
  record(rec: AnalyticsRecord): void;
}

export class MemoryAnalyticsSink implements AnalyticsSink {
  readonly records: AnalyticsRecord[] = [];
  record(rec: AnalyticsRecord): void {
    this.records.push(rec);
  }
  countOf(kpi: KpiName): number {
    return this.records.filter((r) => r.kpi === kpi).length;
  }
}

/** The headline KPI (GDD §15): did they start a second adventure? */
export function isSecondAdventureStart(runsCompleted: number, runsStarted: number): boolean {
  return runsCompleted >= 1 && runsStarted >= 2;
}

/** Fraction of met NPCs the player deliberately went back to. */
export function npcRevisitRate(state: GameState): number {
  const met = Object.values(state.npcs).filter((n) => n.metCount > 0);
  if (met.length === 0) return 0;
  return met.filter((n) => n.metCount > 1).length / met.length;
}

export function choiceReplayRate(log: EventLog): number {
  const choices = log.ofType('CHOICE_MADE');
  if (choices.length === 0) return 0;
  const byEncounter = new Map<string, Set<string>>();
  for (const e of choices) {
    const enc = typeof e.data?.['encounterId'] === 'string' ? e.data['encounterId'] : 'unknown';
    const choice = typeof e.data?.['choiceId'] === 'string' ? e.data['choiceId'] : 'unknown';
    (byEncounter.get(enc) ?? byEncounter.set(enc, new Set()).get(enc)!).add(choice);
  }
  const replayed = [...byEncounter.values()].filter((s) => s.size > 1).length;
  return replayed / byEncounter.size;
}
